import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlogPost } from '../types';
import BlogCard from './BlogCard';
import { fetchHomeBlogs } from '../services/api';


const ExplorePage: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const blogs = await fetchHomeBlogs();
        setPosts(blogs);
        setError(null);
      } catch (err) {
        console.error('Failed to load posts:', err);
        setError('Failed to load posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  // Get featured article (first article when no filters are applied)
  const featuredPost = useMemo(() => {
    return posts[0] || null;
  }, [posts]);

  // Get remaining posts (excluding featured post)
  const remainingPosts = useMemo(() => {
    if (!featuredPost) return posts;
    return posts.filter(post => post.id !== featuredPost.id);
  }, [posts, featuredPost]);

  if (loading) {
    return (
      <div className="home-page">
        <div className="home-content">
          <div className="loading-state">Loading posts...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-page">
        <div className="home-content">
          <div className="error-state">
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="btn btn-primary">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="home-content">
        <main className="posts-section">
          {featuredPost && (
            <div className="featured-article">
              <div className="featured-article-content" onClick={() => navigate(`/blog/${featuredPost.slug || featuredPost.id}`)}>
                <h1 className="featured-title">{featuredPost.title || 'Untitled'}</h1>
                <div className="featured-excerpt">{featuredPost.subtitle}</div>
                <div className="featured-author-row">
                  <div className="featured-author" onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/users/${featuredPost.author_username}`);
                  }}>
                    <div className="featured-author-name">@{featuredPost.author_username}</div>
                  </div>
                </div>
                <div className="featured-bottom-row">
                  <div className="featured-stats">
                    <span className="featured-date">
                      {new Date(featuredPost.posted_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="featured-separator">·</span>
                    <span className="featured-separator">·</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="posts-header">
            <h2 className="posts-count">
              {featuredPost ? 'More stories' : `${posts.length} ${posts.length === 1 ? 'post' : 'posts'}`}
            </h2>
          </div>

          <div className="posts-grid">
            {(featuredPost ? remainingPosts : posts).length > 0 &&
              (featuredPost ? remainingPosts : posts).map(post => (
                <BlogCard
                  key={post.id}
                  post={post}
                  onClick={() => navigate(`/blog/${post.slug || post.id}`)}
                  onProfileView={(username) => navigate(`/users/${username}`)}
                />
              ))
            }
          </div>
        </main>
      </div>
    </div>
  );
};

export default ExplorePage;